import { Inject, Injectable } from '@angular/core';

/**
 * Minimal typings for the AWS WAF integration SDK loaded from the WAF console's
 * "Application integration" URL. `AwsWafIntegration.getToken()` runs the silent
 * Challenge and returns a token; `AwsWafCaptcha.renderCaptcha()` renders an inline
 * puzzle (only used when WAF escalates a flagged request).
 */
interface AwsWafIntegration {
  getToken(): Promise<string>;
  hasToken?(): boolean;
}
interface AwsWafCaptcha {
  renderCaptcha(
    container: HTMLElement,
    options: {
      apiKey: string;
      onSuccess: (token: string) => void;
      onError?: (error: unknown) => void;
      onLoad?: () => void;
    }
  ): void;
}
declare global {
  interface Window {
    AwsWafIntegration?: AwsWafIntegration;
    AwsWafCaptcha?: AwsWafCaptcha;
  }
}

/**
 * Wraps the AWS WAF CAPTCHA/Challenge JavaScript integration for the public intake form.
 *
 * Bot protection is enforced at the edge by AWS WAF on the API ALB. This service acquires a
 * WAF token silently on load (the Challenge action — no visible puzzle for legitimate users)
 * so the {@link WafTokenInterceptor} can attach it to intake API calls as `x-aws-waf-token`.
 * If a request is nonetheless flagged, the interceptor escalates to a visible CAPTCHA via
 * {@link renderCaptcha}.
 *
 * Config (from the intake app environment):
 *   - `waf_captcha.jsapiUrl`: the SDK script URL from the WAF console (empty ⇒ disabled no-op).
 *   - `waf_captcha.apiKey`:   the CAPTCHA API key (token domains scoped to the apex, so the
 *                             token is valid across the tenant subdomain and `api.*`).
 *
 * When `jsapiUrl` is blank the service is disabled: every method resolves to null / no-op so the
 * intake form keeps working during the rollout window before the WAF integration exists. The
 * `apiKey` is only needed for the visible-CAPTCHA escalation, not the silent Challenge flow.
 */
@Injectable({ providedIn: 'root' })
export class WafCaptchaService {
  private readonly jsapiUrl: string;
  private readonly apiKey: string;
  private scriptPromise: Promise<boolean> | null = null;

  constructor(@Inject('environment') environment: { waf_captcha?: { jsapiUrl?: string; apiKey?: string } }) {
    this.jsapiUrl = environment?.waf_captcha?.jsapiUrl ?? '';
    this.apiKey = environment?.waf_captcha?.apiKey ?? '';
  }

  /**
   * True once the SDK URL is configured. Silent token acquisition ({@link getToken}) needs only
   * the SDK; the {@link apiKey} is required solely for the visible {@link renderCaptcha} escalation.
   */
  get enabled(): boolean {
    return !!this.jsapiUrl;
  }

  /**
   * Silently acquire a WAF token (Challenge action). Returns null when disabled or on any
   * failure — callers proceed without a header and let the edge WAF decide (it may then serve
   * the escalation path handled by {@link renderCaptcha}).
   */
  async getToken(): Promise<string | null> {
    if (!this.enabled) {
      return null;
    }
    try {
      const loaded = await this.ensureScript();
      if (!loaded || !window.AwsWafIntegration) {
        return null;
      }
      return await window.AwsWafIntegration.getToken();
    } catch {
      return null;
    }
  }

  /**
   * Render a visible CAPTCHA puzzle inline in [container] (escalation only). Resolves with the
   * solved token, or rejects if disabled/failed.
   */
  async renderCaptcha(container: HTMLElement): Promise<string> {
    if (!this.enabled) {
      throw new Error('WAF captcha is not configured');
    }
    if (!this.apiKey) {
      throw new Error('WAF captcha API key is not configured');
    }
    const loaded = await this.ensureScript();
    const captcha = window.AwsWafCaptcha;
    if (!loaded || !captcha) {
      throw new Error('WAF captcha SDK failed to load');
    }
    return new Promise<string>((resolve, reject) => {
      captcha.renderCaptcha(container, {
        apiKey: this.apiKey,
        onSuccess: (token: string) => resolve(token),
        onError: (error: unknown) =>
          reject(error instanceof Error ? error : new Error('WAF captcha challenge failed'))
      });
    });
  }

  /** Lazily inject the WAF SDK <script> once; memoized. */
  private ensureScript(): Promise<boolean> {
    if (window.AwsWafIntegration) {
      return Promise.resolve(true);
    }
    if (this.scriptPromise) {
      return this.scriptPromise;
    }
    this.scriptPromise = new Promise<boolean>((resolve) => {
      const script = document.createElement('script');
      script.src = this.jsapiUrl;
      script.defer = true;
      script.onload = () => resolve(true);
      script.onerror = () => {
        // Don't memoize a failed load — allow retry after a transient network/CSP failure.
        this.scriptPromise = null;
        resolve(false);
      };
      document.head.appendChild(script);
    });
    return this.scriptPromise;
  }
}
